/**
 * Get appropriate Tabler icon for file type
 * @param filename - The filename
 * @returns Tabler icon name
 */
const getFileExtension = (filename: string): string => {
  return filename.toLowerCase().split('.').pop() || ''
}

export const getFileIcon = (filename: string): string => {
  if (!filename) return 'tabler-file'

  const ext = getFileExtension(filename)

  const iconMap: Record<string, string> = {
    // PDF
    pdf: 'tabler-file-type-pdf',

    // AI
    ai: 'tabler-file-ai',

    // Images
    jpg: 'tabler-file-type-jpg',
    jpeg: 'tabler-file-type-jpg',
    png: 'tabler-file-type-png',
    svg: 'tabler-file-type-svg',
    heic: 'tabler-file-type-jpg',
    heif: 'tabler-file-type-jpg',

    // Documents
    doc: 'tabler-file-type-doc',
    docx: 'tabler-file-type-docx',

    // Spreadsheets
    xls: 'tabler-file-type-xls',
    xlsx: 'tabler-file-type-xls',
    csv: 'tabler-file-type-csv',

    // Presentations
    ppt: 'tabler-file-type-ppt',
    pptx: 'tabler-file-type-ppt',

    // Archives
    zip: 'tabler-file-zip',

    // Text files
    txt: 'tabler-file-type-txt',
  }

  return iconMap[ext] || 'tabler-file'
}

/**
 * Get color for file extension icon.
 * Uses fixed colors for common extensions and a deterministic fallback for unknown ones.
 */
export const getFileIconColor = (filename: string): string => {
  if (!filename) {
    return '#6b7280'
  }

  const ext = getFileExtension(filename)

  const colorMap: Record<string, string> = {
    pdf: '#e53935',
    ai: '#ff8f00',
    jpg: '#1e88e5',
    jpeg: '#1976d2',
    png: '#26a69a',
    heic: '#00897b',
    heif: '#00695c',
    svg: '#8e24aa',
    doc: '#3949ab',
    docx: '#283593',
    xls: '#2e7d32',
    xlsx: '#1b5e20',
    csv: '#43a047',
    ppt: '#ef6c00',
    pptx: '#f4511e',
    zip: '#5d4037',
    txt: '#546e7a',
  }

  if (colorMap[ext]) {
    return colorMap[ext]
  }

  // Deterministic fallback so unknown extensions still get differentiated colors.
  let hash = 0
  for (let i = 0; i < ext.length; i++) {
    hash = (hash << 5) - hash + ext.charCodeAt(i)
    hash |= 0
  }

  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 65%, 45%)`
}
